import React, { useState, useEffect } from 'react';
import { getWhitelist, addToWhitelist, removeFromWhitelist } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, Globe, Loader2, AlertTriangle, Server } from 'lucide-react';

const SecurityPage = () => {
  const [whitelist, setWhitelist] = useState([]);
  const [sgId, setSgId] = useState('');
  const [isRestricted, setIsRestricted] = useState(false);
  const [managedPorts, setManagedPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => { fetchWhitelist(); }, []);

  const fetchWhitelist = async () => {
    try {
      const data = await getWhitelist();
      setWhitelist(data.ips || []);
      setSgId(data.security_group_id || '');
      setIsRestricted(data.is_restricted || false);
      setManagedPorts(data.managed_ports || []);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to load whitelist';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newIp.trim()) return;
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(newIp.trim())) {
      toast.error('Please enter a valid IP address (e.g. 94.204.209.18)');
      return;
    }
    setAdding(true);
    try {
      const result = await addToWhitelist(newIp.trim(), description.trim());
      setWhitelist(result.ips || []);
      setIsRestricted(result.is_restricted || false);
      setNewIp('');
      setDescription('');
      toast.success(result.message || `IP ${newIp.trim()} whitelisted`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to add IP';
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ip) => {
    try {
      const result = await removeFromWhitelist(ip);
      setWhitelist(result.ips || []);
      setIsRestricted(result.is_restricted || false);
      toast.success(result.message || `IP ${ip} removed`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to remove IP';
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="security-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Security — IP Whitelisting
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Only whitelisted IPs can access the portal. Managed via AWS Security Group.
        </p>
      </div>

      {/* Status Banner */}
      {isRestricted ? (
        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3">
          <Shield className="w-5 h-5 text-emerald-600" />
          <div>
            <p className="text-emerald-800 font-medium text-sm">IP Whitelisting Active</p>
            <p className="text-emerald-600 text-xs">Only whitelisted IPs can access ports {managedPorts.join(', ')}. SSH (port 22) is unaffected.</p>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="text-amber-800 font-medium text-sm">Portal is Open to All IPs</p>
            <p className="text-amber-600 text-xs">Add an IP below to activate whitelisting. This will remove the 0.0.0.0/0 open access rules on ports {managedPorts.join(', ')}.</p>
          </div>
        </div>
      )}

      {/* AWS Info */}
      <div className="p-3 rounded-lg bg-gray-50 border border-gray-100 flex items-center gap-3">
        <Server className="w-5 h-5 text-gray-400" />
        <div className="text-sm">
          <span className="text-gray-500">Security Group: </span>
          <span className="text-gray-900 font-mono">{sgId}</span>
          <span className="text-gray-400 ml-3">Ports: </span>
          <span className="text-gray-700 font-mono">{managedPorts.join(', ')}</span>
        </div>
      </div>

      {/* Add IP Form */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#D4AF37]" />
            Add IP Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex gap-3 items-end">
            <div className="flex-1 space-y-2">
              <Label className="text-gray-700">IP Address</Label>
              <Input
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder="e.g. 94.204.209.18"
                className="bg-gray-50 border-gray-200 text-gray-900 font-mono"
                data-testid="whitelist-ip-input"
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-gray-700">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Office network"
                className="bg-gray-50 border-gray-200 text-gray-900"
                data-testid="whitelist-desc-input"
              />
            </div>
            <Button type="submit" disabled={adding || !newIp.trim()} className="btn-gold" data-testid="whitelist-add-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Whitelist</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Current Whitelist */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            Whitelisted IPs ({whitelist.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {whitelist.length > 0 ? (
            <div className="space-y-2">
              {whitelist.map((item) => (
                <div key={item.ip || item.cidr} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-[#D4AF37]" />
                    <span className="font-mono text-gray-900" data-testid={`whitelist-ip-${item.ip}`}>{item.ip}</span>
                    {item.description && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{item.description}</span>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`whitelist-remove-${item.ip}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-white border-gray-200">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-gray-900">Remove IP?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-500">
                          {whitelist.length <= 1 ? (
                            <span className="text-red-600 font-medium">This is the last whitelisted IP! Removing it will lock everyone out.</span>
                          ) : (
                            <>Remove <span className="font-mono font-medium">{item.ip}</span> from the whitelist? They will lose access to the portal.</>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-gray-200 text-gray-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleRemove(item.ip)} className="bg-red-600 hover:bg-red-700 text-white">
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No IPs whitelisted yet</p>
              <p className="text-gray-500 text-sm mt-1">Add your IP above to activate whitelisting. Open access rules (0.0.0.0/0) will be removed automatically.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recovery Note */}
      <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Locked out?</p>
            <p className="mt-1">If your IP changes and you lose access, you can always restore it through:</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-amber-700">
              <li>AWS Console → EC2 → Security Groups → Edit inbound rules</li>
              <li>AWS CLI: <code className="bg-amber-100 px-1 rounded text-xs">aws ec2 authorize-security-group-ingress --group-id {sgId} --protocol tcp --port 80 --cidr YOUR_IP/32</code></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
